import { BLACK, WHITE, opposite, type Color } from '../primitives/color.js'
import {
  BISHOP,
  EMPTY,
  KING,
  KNIGHT,
  PAWN,
  PROMOTION_PIECES,
  QUEEN,
  ROOK,
  codeColor,
  codeType,
  encodePiece,
  type PieceCode,
  type PieceType,
} from '../primitives/piece.js'
import { isOnBoard, rankOf, sq, square, type Square } from '../primitives/square.js'
import type { CastleSide, Move } from '../position/move.js'
import {
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_WK,
  CASTLE_WQ,
  clonePosition,
  makeMoveInPlace,
  undoMoveInPlace,
  type MutablePosition,
  type Position,
} from '../position/position.js'
import {
  BISHOP_DELTAS,
  KING_DELTAS,
  KNIGHT_DELTAS,
  PAWN_CAPTURES,
  PAWN_PROMOTION_RANK,
  PAWN_PUSH,
  PAWN_START_RANK,
  QUEEN_DELTAS,
  ROOK_DELTAS,
  isAligned,
  isSquareAttacked,
} from './attacks.js'

interface CastleSpec {
  readonly side: CastleSide
  readonly right: number
  readonly kingFrom: Square
  readonly kingTo: Square
  readonly rookFrom: Square
  readonly rookTo: Square
  /**
   * Squares that must be vacant. Note this includes b1/b8 for queenside — the
   * *rook* travels through it — even though the king never touches it and it
   * therefore does not need to be safe.
   */
  readonly vacant: readonly Square[]
  /** Squares the king occupies or crosses; each must be unattacked. */
  readonly unattacked: readonly Square[]
}

const CASTLES: readonly (readonly CastleSpec[])[] = [
  [
    {
      side: 'king',
      right: CASTLE_WK,
      kingFrom: sq('e1'),
      kingTo: sq('g1'),
      rookFrom: sq('h1'),
      rookTo: sq('f1'),
      vacant: [sq('f1'), sq('g1')],
      unattacked: [sq('e1'), sq('f1'), sq('g1')],
    },
    {
      side: 'queen',
      right: CASTLE_WQ,
      kingFrom: sq('e1'),
      kingTo: sq('c1'),
      rookFrom: sq('a1'),
      rookTo: sq('d1'),
      vacant: [sq('d1'), sq('c1'), sq('b1')],
      unattacked: [sq('e1'), sq('d1'), sq('c1')],
    },
  ],
  [
    {
      side: 'king',
      right: CASTLE_BK,
      kingFrom: sq('e8'),
      kingTo: sq('g8'),
      rookFrom: sq('h8'),
      rookTo: sq('f8'),
      vacant: [sq('f8'), sq('g8')],
      unattacked: [sq('e8'), sq('f8'), sq('g8')],
    },
    {
      side: 'queen',
      right: CASTLE_BQ,
      kingFrom: sq('e8'),
      kingTo: sq('c8'),
      rookFrom: sq('a8'),
      rookTo: sq('d8'),
      vacant: [sq('d8'), sq('c8'), sq('b8')],
      unattacked: [sq('e8'), sq('d8'), sq('c8')],
    },
  ],
]

const SLIDER_DELTAS: Partial<Record<PieceType, readonly number[]>> = {
  [BISHOP]: BISHOP_DELTAS,
  [ROOK]: ROOK_DELTAS,
  [QUEEN]: QUEEN_DELTAS,
}

const pushPawnMoves = (out: Move[], from: Square, to: Square, captured: PieceType | null, us: Color): void => {
  if (rankOf(to) === PAWN_PROMOTION_RANK[us]) {
    for (const promoteTo of PROMOTION_PIECES) out.push({ kind: 'promotion', from, to, promoteTo, captured })
    return
  }
  out.push({ kind: 'normal', from, to, piece: PAWN, captured })
}

const generatePawn = (pos: Position, from: Square, us: Color, out: Move[]): void => {
  const { board } = pos
  const push = PAWN_PUSH[us]

  const oneAhead = from + push
  if (isOnBoard(oneAhead) && board[oneAhead] === EMPTY) {
    pushPawnMoves(out, from, oneAhead as Square, null, us)

    // The double push needs the intermediate square empty too, which the check
    // above has already established.
    if (rankOf(from) === PAWN_START_RANK[us]) {
      const twoAhead = from + push * 2
      if (board[twoAhead] === EMPTY) {
        out.push({ kind: 'doublePush', from, to: twoAhead as Square })
      }
    }
  }

  for (const delta of PAWN_CAPTURES[us]) {
    const to = from + delta
    if (!isOnBoard(to)) continue
    const code = board[to] as PieceCode
    if (code !== EMPTY) {
      if (codeColor(code) !== us) pushPawnMoves(out, from, to as Square, codeType(code), us)
      continue
    }
    if (pos.epSquare !== null && to === pos.epSquare) {
      out.push({ kind: 'enPassant', from, to: to as Square })
    }
  }
}

const generateStepper = (
  pos: Position,
  from: Square,
  us: Color,
  piece: PieceType,
  deltas: readonly number[],
  out: Move[],
): void => {
  const { board } = pos
  for (const delta of deltas) {
    const to = from + delta
    if (!isOnBoard(to)) continue
    const code = board[to] as PieceCode
    if (code === EMPTY) {
      out.push({ kind: 'normal', from, to: to as Square, piece, captured: null })
    } else if (codeColor(code) !== us) {
      out.push({ kind: 'normal', from, to: to as Square, piece, captured: codeType(code) })
    }
  }
}

const generateSlider = (
  pos: Position,
  from: Square,
  us: Color,
  piece: PieceType,
  deltas: readonly number[],
  out: Move[],
): void => {
  const { board } = pos
  for (const delta of deltas) {
    let to = from + delta
    while (isOnBoard(to)) {
      const code = board[to] as PieceCode
      if (code === EMPTY) {
        out.push({ kind: 'normal', from, to: to as Square, piece, captured: null })
        to += delta
        continue
      }
      if (codeColor(code) !== us) {
        out.push({ kind: 'normal', from, to: to as Square, piece, captured: codeType(code) })
      }
      break
    }
  }
}

const generateCastles = (pos: Position, us: Color, out: Move[]): void => {
  const { board } = pos
  const them = opposite(us)
  for (const spec of CASTLES[us] as readonly CastleSpec[]) {
    if ((pos.castling & spec.right) === 0) continue
    if (spec.vacant.some((s) => board[s] !== EMPTY)) continue
    // Castling out of, through, or into check is all forbidden — and all three
    // fall out of checking every square the king touches.
    if (spec.unattacked.some((s) => isSquareAttacked(pos, s, them))) continue
    out.push({
      kind: 'castle',
      from: spec.kingFrom,
      to: spec.kingTo,
      side: spec.side,
      rookFrom: spec.rookFrom,
      rookTo: spec.rookTo,
    })
  }
}

/**
 * Every move that looks right for the piece, ignoring whether it leaves the
 * mover's own king in check. Castling is the exception: its safety conditions
 * are checked here because they concern squares the king merely passes over,
 * which no after-the-fact king-safety test would catch.
 */
export const generatePseudoLegalMoves = (pos: Position): Move[] => {
  const out: Move[] = []
  const us = pos.turn
  const { board } = pos

  for (let rank = 0; rank < 8; rank += 1) {
    for (let file = 0; file < 8; file += 1) {
      const from = square(file, rank)
      const code = board[from] as PieceCode
      if (code === EMPTY || codeColor(code) !== us) continue

      const type = codeType(code)
      switch (type) {
        case PAWN:
          generatePawn(pos, from, us, out)
          break
        case KNIGHT:
          generateStepper(pos, from, us, KNIGHT, KNIGHT_DELTAS, out)
          break
        case KING:
          generateStepper(pos, from, us, KING, KING_DELTAS, out)
          break
        default:
          generateSlider(pos, from, us, type, SLIDER_DELTAS[type] as readonly number[], out)
      }
    }
  }

  generateCastles(pos, us, out)
  return out
}

/**
 * Filters pseudo-legal moves down to legal ones by playing each and asking
 * whether our king is attacked.
 *
 * The test is skipped when it provably cannot fail: if we are not in check, the
 * moving piece is not the king, the move is not en passant, and the piece is
 * not on a line with our king, then nothing it does can expose the king. That
 * covers the large majority of moves in a typical position.
 *
 * Mutates `pos` during the scan but restores it exactly, so it is safe to call
 * on a position you are recursing over.
 */
export const generateLegalMovesMutable = (pos: MutablePosition): Move[] => {
  const us = pos.turn
  const them = opposite(us)
  const kingSquare = pos.kings[us]
  const inCheck = isSquareAttacked(pos, kingSquare, them)

  const legal: Move[] = []
  for (const move of generatePseudoLegalMoves(pos)) {
    const mustVerify =
      inCheck || move.from === kingSquare || move.kind === 'enPassant' || isAligned(move.from, kingSquare)

    if (!mustVerify) {
      legal.push(move)
      continue
    }

    const undo = makeMoveInPlace(pos, move)
    const safe = !isSquareAttacked(pos, pos.kings[us], them)
    undoMoveInPlace(pos, move, undo)
    if (safe) legal.push(move)
  }
  return legal
}

/** The pure entry point. Clones once so the caller's position is untouched. */
export const generateLegalMoves = (pos: Position): Move[] =>
  generateLegalMovesMutable(clonePosition(pos))

export const hasLegalMoves = (pos: Position): boolean => generateLegalMoves(pos).length > 0

/** Sanity check used by FEN-driven entry points: is this position playable? */
export const isPositionPlayable = (pos: Position): boolean => {
  // The side that just moved must not have left its own king en prise.
  const previous = opposite(pos.turn)
  if (isSquareAttacked(pos, pos.kings[previous], pos.turn)) return false

  // Pawns cannot legally stand on the back ranks.
  for (const color of [WHITE, BLACK] as const) {
    const pawn = encodePiece(color, PAWN)
    for (const rank of [0, 7] as const) {
      for (let file = 0; file < 8; file += 1) {
        if (pos.board[square(file, rank)] === pawn) return false
      }
    }
  }
  return true
}
